import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const UserContext = createContext(null);

// Hook for using the user context
export const useUsers = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUsers must be used within a UserProvider');
  }
  return context;
};

// Provider component
export const UserProvider = ({ children, users = [] }) => {
  const [userMap, setUserMap] = useState({});

  // Update userMap when users prop changes
  useEffect(() => {
    console.log('[UserContext] useEffect triggered with users:', users.length);
    
    if (!users || users.length === 0) {
      console.warn('[UserContext] No users provided to UserProvider');
      return;
    }
    
    const map = {};
    users.forEach(user => {
      if (user.uid) {
        map[user.uid] = user;
      }
    });
    
    console.log('[UserContext] Updating userMap with users:', users.length);
    console.log('[UserContext] First few users:', users.slice(0, 3));
    console.log('[UserContext] New userMap keys:', Object.keys(map).length);
    
    setUserMap(map);
  }, [users]);

  // Function to get user display name
  const getUserDisplayName = (userId) => {
    console.log('[UserContext] getUserDisplayName called with:', userId);
    
    if (!userId) {
      console.log('[UserContext] getUserDisplayName - userId is empty');
      return '';
    }
    
    // If userId is already a user object
    if (typeof userId === 'object') {
      console.log('[UserContext] userId is an object:', userId);
      const name = userId.displayName || userId.email || '';
      const result = name.includes('@') ? name.split('@')[0] : name;
      console.log('[UserContext] Resolved from object to:', result);
      return result;
    }
    
    // Look up user by ID
    const user = userMap[userId];
    console.log('[UserContext] User lookup result:', user);
    
    if (user) {
      const name = user.displayName || user.email || '';
      const result = name.includes('@') ? name.split('@')[0] : name;
      console.log('[UserContext] Resolved from userMap to:', result);
      return result;
    }
    
    // Fallback: If userId looks like an email, extract the part before @
    if (typeof userId === 'string' && userId.includes('@')) {
      const result = userId.split('@')[0];
      console.log('[UserContext] Resolved from email string to:', result);
      return result;
    }
    
    // For debugging - log the missing user
    console.log('[UserContext] User not found in userMap:', userId);
    console.log('[UserContext] Current userMap keys:', Object.keys(userMap));
    
    // Return just the first part of the ID without the "User" prefix
    const result = userId.substring(0, 8);
    console.log('[UserContext] Fallback to substring:', result);
    return result;
  };
  
  return (
    <UserContext.Provider value={{ users, userMap, getUserDisplayName }}>
      {children}
    </UserContext.Provider>
  );
};
